'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useLabsStore, usePack } from '../pack-context';
import { SLIDER_CENTER, SLIDER_STOPS, sliderSide } from '../scoring';
import { StepNav } from './_components';
import { StepShell } from './step-shell';

/**
 * M2 · Activity — the mindset slider quiz (THE BRANCH POINT: this screen's answers resolve the
 * learner's archetype). Styled to the SOURCE vanilla prototype (`data-view="m2"` + `.m2-question-card`
 * / `.m2-slider*` in styles.css), rendered through the Extern finance-sprint design system (fs-*
 * tokens + DM Serif Display / Inter).
 *
 * LAYOUT (from the HTML prototype):
 *   - One question at a time, centered: a mono uppercase "Question N of M" counter (`.q-counter`), a
 *     serif question headline (`.m2-question-card h3`, ~2.5rem), then the slider block
 *     (`.m2-slider-wrap`, max-w 640): justified end labels (the two anchors), ONE full-width gradient
 *     slider (pole-A → muted → pole-B, 6px tall, 44px white thumb with a 3px ink border), a row of 7
 *     tick marks under the track, then the next-hint line.
 *   - NO fixed bottom bar on M2 in the SOURCE — the quiz auto-advances on release and the (pre-reveal)
 *     Back is a quiet link. Here the shared chrome owns Back (top-left, via StepShell); a single
 *     primary "Continue" stays in the body to commit explicitly without waiting for the timer.
 *
 * SLIDER: a RAW native `<input type="range">` with a decorative thumb/track overlay (`@extern/ui` has
 * no slider primitive, and none is needed — the native range IS the accessible drag/keyboard control).
 * The visible thumb is `aria-hidden` decoration; every semantic (role, min/max/now, valuetext, and the
 * question as the label) rides on the real input, which sits transparent on top of the track.
 *
 * THE GRADIENT IS THE AXIS. The track runs from pole A's accent through the muted mid-stop to pole B's
 * — read from `axis.poles`, never hardcoded. The shipped finance screen bakes in `CORP_GREEN` /
 * `INVEST_PURPLE` hex constants, which is exactly the coupling the axis exists to remove: the same
 * two accents theme M1's lean bar and the archetype reveal, so they are declared once, by the pack.
 *
 * Behaviour (faithful to the prototype's app.js): each question is a 0–6 range slider (7 stops).
 * Releasing the slider records the answer via `setM2Answer` (which re-derives the archetype), then
 * auto-advances ~500ms later; the explicit "Continue" commits immediately at the current value. When
 * the LAST question is committed, advance to `m2-results` (the archetype reveal) exactly once.
 *
 * Token mapping (SOURCE intent → fs-* token / decoration):
 *   - source thumb #FAFAF7 + 3px #0A0E1A border → `fs-surface` fill + `fs-text`-ink border
 *   - source `.q-counter` mono mute #6B7280 → `fs-text-muted`; `.m2-slider-labels` #3A4252 → `fs-text-subtle`
 *   - source `.m2-next-hint` #6B7280 → `fs-text-muted`; tick marks #E6E4DD → `fs-border`
 */

const AUTO_ADVANCE_MS = 500;
const TICKS = Array.from({
  length: SLIDER_STOPS
}, (_, i) => i);

/** The gradient's mid-stop — the "no strong lean" centre. The poles either side of it come from the axis. */
const TRACK_MUTED = 'var(--fs-muted)';

/** Keys that, on key-up, commit the current slider value (keyboard parity with pointer release). */
const COMMIT_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);

/** Accessible announcement for the current slider position (avoids a nested ternary in JSX). */
function answerValueText(value: number, leftLabel: string, rightLabel: string): string {
  const side = sliderSide(value);
  if (side === 'left') {
    return leftLabel;
  }
  if (side === 'right') {
    return rightLabel;
  }
  return 'Split — no strong lean';
}
export function M2Activity() {
  const $ = _c(79);
  const {
    axis,
    m2
  } = usePack();
  const questions = m2.questions;
  const m2Answers = useLabsStore(_temp);
  const setM2Answer = useLabsStore(_temp2);
  const next = useLabsStore(_temp3);
  const back = useLabsStore(_temp4);
  let t0;
  if ($[0] !== m2Answers || $[1] !== questions) {
    t0 = () => {
      let i = 0;
      while (i < questions.length && m2Answers[questions[i]?.id ?? ""] !== undefined) {
        i = i + 1;
      }
      return Math.min(i, questions.length - 1);
    };
    $[0] = m2Answers;
    $[1] = questions;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [cursor, setCursor] = useState(t0);
  const question = questions[cursor] ?? questions[0];
  const questionId = question?.id ?? "";
  const recorded = m2Answers[questionId];
  const [value, setValue] = useState(recorded ?? SLIDER_CENTER);
  const [touched, setTouched] = useState(recorded !== undefined);
  let t1;
  if ($[3] !== m2Answers || $[4] !== questionId) {
    t1 = () => {
      setValue(m2Answers[questionId] ?? SLIDER_CENTER);
      setTouched(m2Answers[questionId] !== undefined);
    };
    $[3] = m2Answers;
    $[4] = questionId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== cursor || $[7] !== questionId) {
    t2 = [cursor, questionId];
    $[6] = cursor;
    $[7] = questionId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  useEffect(t1, t2);
  const advancedRef = useRef(false);
  const advanceTimer = useRef(null);
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (advanceTimer.current) {
        clearTimeout(advanceTimer.current);
        advanceTimer.current = null;
      }
    };
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const clearAdvanceTimer = t3;
  let t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      if (advanceTimer.current) {
        clearTimeout(advanceTimer.current);
      }
    };
    t5 = [];
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] !== cursor || $[13] !== next || $[14] !== questionId || $[15] !== questions.length || $[16] !== setM2Answer) {
    t6 = v => {
      if (!questionId) {
        return;
      }
      setM2Answer(questionId, v);
      if (advanceTimer.current) {
        clearTimeout(advanceTimer.current);
      }
      advanceTimer.current = setTimeout(() => {
        if (cursor < questions.length - 1) {
          setCursor(c => Math.min(c + 1, questions.length - 1));
        } else {
          if (!advancedRef.current) {
            advancedRef.current = true;
            next();
          }
        }
      }, AUTO_ADVANCE_MS);
    };
    $[12] = cursor;
    $[13] = next;
    $[14] = questionId;
    $[15] = questions.length;
    $[16] = setM2Answer;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const commit = t6;
  let t7;
  if ($[18] !== commit || $[19] !== value) {
    t7 = () => {
      clearAdvanceTimer();
      commit(value);
    };
    $[18] = commit;
    $[19] = value;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleContinue = t7;
  let t8;
  if ($[21] !== back) {
    t8 = () => {
      clearAdvanceTimer();
      back();
    };
    $[21] = back;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleBack = t8;
  const t9 = question?.leftLabel ?? "";
  const t10 = question?.rightLabel ?? "";
  let t11;
  if ($[23] !== t10 || $[24] !== t9 || $[25] !== value) {
    t11 = answerValueText(value, t9, t10);
    $[23] = t10;
    $[24] = t9;
    $[25] = value;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const answerText = t11;
  const thumbPct = value / (SLIDER_STOPS - 1) * 100;
  let t12;
  if ($[27] !== m2Answers) {
    t12 = Object.keys(m2Answers);
    $[27] = m2Answers;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const answeredCount = t12.length;
  const t13 = cursor + 1;
  let t14;
  if ($[29] !== questions.length || $[30] !== t13) {
    t14 = <div className="font-mono text-[11px] text-fs-text-muted uppercase tracking-[0.18em]">Question {t13} of {questions.length}</div>;
    $[29] = questions.length;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = question?.q;
  let t16;
  if ($[32] !== t15) {
    t16 = <h1 className="text-balance font-instrument text-fs-display-sm text-fs-text leading-[1.15] sm:text-fs-display-md">{t15}</h1>;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t14 || $[35] !== t16) {
    t17 = <div className="flex w-full flex-col items-center gap-6">{t14}{t16}</div>;
    $[34] = t14;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const t18 = question?.leftLabel;
  let t19;
  if ($[37] !== t18) {
    t19 = <span className="max-w-[45%] text-left">{t18}</span>;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const t20 = question?.rightLabel;
  let t21;
  if ($[39] !== t20) {
    t21 = <span className="max-w-[45%] text-right">{t20}</span>;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t19 || $[42] !== t21) {
    t22 = <div className="flex items-start justify-between gap-4 text-balance text-fs-body-md text-fs-text-subtle leading-[1.4]">{t19}{t21}</div>;
    $[41] = t19;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = `linear-gradient(90deg, ${axis.poles.a.accent} 0%, ${TRACK_MUTED} 50%, ${axis.poles.b.accent} 100%)`;
  let t24;
  if ($[44] !== t23) {
    t24 = <div className="h-1.5 w-full rounded-[3px]" style={{
      backgroundImage: t23
    }} />;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  const t25 = `${thumbPct}%`;
  let t26;
  if ($[46] !== t25) {
    t26 = <div aria-hidden={true} className="pointer-events-none absolute top-1/2 size-11 -translate-x-1/2 -translate-y-1/2 rounded-fs-pill border-[3px] border-fs-text bg-fs-surface shadow-[0_2px_8px_rgba(10,14,26,0.25)]" style={{
      left: t25
    }} />;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  const t27 = question?.q;
  let t28;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = e => {
      setValue(Number(e.target.value));
      setTouched(true);
    };
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  let t30;
  if ($[49] !== commit) {
    t29 = e_0 => {
      if (COMMIT_KEYS.has(e_0.key)) {
        commit(Number((e_0.target as HTMLInputElement).value));
      }
    };
    t30 = e_1 => commit(Number((e_1.target as HTMLInputElement).value));
    $[49] = commit;
    $[50] = t29;
    $[51] = t30;
  } else {
    t29 = $[50];
    t30 = $[51];
  }
  let t31;
  if ($[52] !== answerText || $[53] !== t27 || $[54] !== t29 || $[55] !== t30 || $[56] !== value) {
    t31 = <input aria-label={t27} aria-valuetext={answerText} className="absolute inset-0 h-11 w-full cursor-grab opacity-0 active:cursor-grabbing" max={SLIDER_STOPS - 1} min={0} onChange={t28} onKeyUp={t29} onPointerUp={t30} step={1} type="range" value={value} />;
    $[52] = answerText;
    $[53] = t27;
    $[54] = t29;
    $[55] = t30;
    $[56] = value;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  let t32;
  if ($[58] !== t24 || $[59] !== t26 || $[60] !== t31) {
    t32 = <div className="relative flex h-11 w-full items-center">{t24}{t26}{t31}</div>;
    $[58] = t24;
    $[59] = t26;
    $[60] = t31;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="flex justify-between px-[11px]">{TICKS.map(_temp5)}</div>;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  const t34 = touched ? "Release to continue." : "Drag the slider to answer.";
  let t35;
  if ($[63] !== t34) {
    t35 = <p className="mt-4 h-6 text-fs-body-md text-fs-text-muted">{t34}</p>;
    $[63] = t34;
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  let t36;
  if ($[65] !== t22 || $[66] !== t32 || $[67] !== t35) {
    t36 = <div className="flex w-full flex-col gap-4">{t22}{t32}{t33}{t35}</div>;
    $[65] = t22;
    $[66] = t32;
    $[67] = t35;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  let t37;
  if ($[69] !== handleBack || $[70] !== handleContinue) {
    t37 = <StepNav onBack={handleBack} onNext={handleContinue} />;
    $[69] = handleBack;
    $[70] = handleContinue;
    $[71] = t37;
  } else {
    t37 = $[71];
  }
  let t38;
  if ($[72] !== t17 || $[73] !== t36 || $[74] !== t37) {
    t38 = <div className="flex w-full max-w-[640px] flex-col items-center gap-10 text-center">{t17}{t36}{t37}</div>;
    $[72] = t17;
    $[73] = t36;
    $[74] = t37;
    $[75] = t38;
  } else {
    t38 = $[75];
  }
  let t39;
  if ($[76] !== answeredCount || $[77] !== t38) {
    t39 = <StepShell contentClassName="flex flex-col items-center" tasksCompleted={answeredCount}>{t38}</StepShell>;
    $[76] = answeredCount;
    $[77] = t38;
    $[78] = t39;
  } else {
    t39 = $[78];
  }
  return t39;
}
function _temp5(tick) {
  return <span aria-hidden={true} className="block h-1.5 w-px bg-fs-border" key={`tick-${tick}`} />;
}
function _temp4(s_2) {
  return s_2.back;
}
function _temp3(s_1) {
  return s_1.next;
}
function _temp2(s_0) {
  return s_0.setM2Answer;
}
function _temp(s) {
  return s.m2Answers;
}